import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Repository,
  RepositoriesResponse,
  RepositoryProvider,
  RepositoryStatus
} from '../../types/Repository.types';
import { RequirementStep } from '../../types/RequirementSteps.types';

type RepositoryState = {
  repositories: Repository[];
  total: number;
  loading: boolean;
  error: string | null;
  connectLoading: boolean;
  connectError: string | null;
  deleteLoading: boolean;
  deleteError: string | null;
  updateBranchLoading: boolean;
  updateBranchError: string | null;
  syncLoading: Record<string, boolean>;
  syncError: string | null;
  filters: {
    search: string;
    provider: RepositoryProvider[];
    branch: string[];
    status: RepositoryStatus[];
    lastSyncedFrom: string | null;
    lastSyncedTo: string | null;
    showPendingOnly: boolean;
  };
  pagination: {
    page: number;
    limit: number;
  };
  sorting: {
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  };
};

export const isReqListStepStarted = (
  steps: RequirementStep[],
  stepKey: string
) => {
  const stepStatus = steps.find((step) => step.key === stepKey)?.status;
  return stepStatus !== undefined && stepStatus !== 'NOT_STARTED';
};

const initialState: RepositoryState = {
  repositories: [],
  total: 0,
  loading: false,
  error: null,
  connectLoading: false,
  connectError: null,
  deleteLoading: false,
  deleteError: null,
  updateBranchLoading: false,
  updateBranchError: null,
  syncLoading: {},
  syncError: null,
  filters: {
    search: '',
    provider: [],
    branch: [],
    status: [],
    lastSyncedFrom: null,
    lastSyncedTo: null,
    showPendingOnly: false
  },
  pagination: {
    page: 1,
    limit: 10
  },
  sorting: {
    sortBy: undefined,
    sortOrder: undefined
  }
};

export const repositorySlice = createSlice({
  name: 'repository',
  initialState,
  reducers: {
    // Fetch repositories
    fetchRepositoriesStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchRepositoriesSuccess: (
      state,
      action: PayloadAction<RepositoriesResponse>
    ) => {
      state.loading = false;

      // Preserve locally updated branch and syncInProgress for repositories that are syncing
      const newRepositories = action.payload?.data || [];
      const updatedRepositories = newRepositories.map((newRepo) => {
        const existingRepo = state.repositories.find(
          (r) => r.id === newRepo.id
        );

        // If repository was locally updated (e.g., branch change), preserve local changes during sync
        if (existingRepo?.syncInProgress && newRepo.status === 'SYNCING') {
          return {
            ...newRepo,
            localMetadata: existingRepo.localMetadata || newRepo.localMetadata,
            syncInProgress: true
          };
        }

        return newRepo;
      });

      state.repositories = updatedRepositories;
      state.total = action.payload?.total || 0;
      state.error = null;
    },
    appendRepositories: (
      state,
      action: PayloadAction<RepositoriesResponse>
    ) => {
      state.loading = false;
      state.repositories = [
        ...state.repositories,
        ...(action.payload?.data || [])
      ];
      state.total = action.payload?.total || 0;
      state.error = null;
    },
    fetchRepositoriesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Connect repository
    connectRepositoryStart: (state) => {
      state.connectLoading = true;
      state.connectError = null;
    },
    connectRepositorySuccess: (state, action: PayloadAction<Repository>) => {
      state.connectLoading = false;
      state.repositories.push(action.payload);
      state.total += 1;
      state.connectError = null;
    },
    connectRepositoryFailure: (state, action: PayloadAction<string>) => {
      state.connectLoading = false;
      state.connectError = action.payload;
    },

    // Delete repository
    deleteRepositoryStart: (state) => {
      state.deleteLoading = true;
      state.deleteError = null;
    },
    deleteRepositorySuccess: (state, action: PayloadAction<string>) => {
      state.deleteLoading = false;
      state.repositories = state.repositories.filter(
        (repo) => repo.id !== action.payload
      );
      state.total -= 1;
      state.deleteError = null;
    },
    deleteRepositoryFailure: (state, action: PayloadAction<string>) => {
      state.deleteLoading = false;
      state.deleteError = action.payload;
    },

    // Update repository branch
    updateRepositoryBranchStart: (state) => {
      state.updateBranchLoading = true;
      state.updateBranchError = null;
    },
    updateRepositoryBranchSuccess: (
      state,
      action: PayloadAction<{ repositoryId: string; branch: string }>
    ) => {
      state.updateBranchLoading = false;
      const repository = state.repositories.find(
        (repo) => repo.id === action.payload.repositoryId
      );
      if (repository) {
        if (!repository.localMetadata) {
          repository.localMetadata = {
            local_path: '',
            current_branch: action.payload.branch
          };
        } else {
          repository.localMetadata.current_branch = action.payload.branch;
        }
        // Mark repository as syncing after branch change
        repository.status = 'SYNCING';
        repository.syncInProgress = true;
      }
      state.updateBranchError = null;
    },
    updateRepositoryBranchFailure: (state, action: PayloadAction<string>) => {
      state.updateBranchLoading = false;
      state.updateBranchError = action.payload;
    },

    // Sync repository
    syncRepositoryStart: (state, action: PayloadAction<string>) => {
      state.syncLoading[action.payload] = true;
      state.syncError = null;
    },
    syncRepositorySuccess: (state, action: PayloadAction<string>) => {
      state.syncLoading[action.payload] = false;
      const repository = state.repositories.find(
        (repo) => repo.id === action.payload
      );
      if (repository) {
        repository.status = 'SYNCING';
        repository.syncInProgress = true; // Mark sync in progress for polling
      }
      state.syncError = null;
    },
    syncRepositoryFailure: (
      state,
      action: PayloadAction<{ repositoryId: string; error: string }>
    ) => {
      state.syncLoading[action.payload.repositoryId] = false;
      state.syncError = action.payload.error;
    },

    // Clear errors
    clearErrors: (state) => {
      state.error = null;
      state.connectError = null;
      state.deleteError = null;
      state.updateBranchError = null;
      state.syncError = null;
    },

    // Filters
    setSearchFilter: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      // state.repositories = [];
    },
    setProviderFilter: (state, action: PayloadAction<RepositoryProvider[]>) => {
      state.filters.provider = action.payload;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      state.repositories = [];
    },
    setBranchFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.branch = action.payload;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      state.repositories = [];
    },
    setStatusFilter: (state, action: PayloadAction<RepositoryStatus[]>) => {
      state.filters.status = action.payload;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      state.repositories = [];
    },
    setLastSyncedFilter: (
      state,
      action: PayloadAction<{ from: string | null; to: string | null }>
    ) => {
      state.filters.lastSyncedFrom = action.payload.from;
      state.filters.lastSyncedTo = action.payload.to;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      // state.repositories = [];
    },
    setShowPendingOnly: (state, action: PayloadAction<boolean>) => {
      state.filters.showPendingOnly = action.payload;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      state.repositories = [];
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
      state.pagination.limit = 10;
      state.repositories = [];
    },

    // Pagination
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
    },

    // Sorting
    setSorting: (
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: 'asc' | 'desc' }>
    ) => {
      state.sorting.sortBy = action.payload.sortBy;
      state.sorting.sortOrder = action.payload.sortOrder;
    },

    // Reset state
    resetRepository: () => initialState,

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    }
  }
});

export const {
  fetchRepositoriesStart,
  fetchRepositoriesSuccess,
  appendRepositories,
  fetchRepositoriesFailure,
  connectRepositoryStart,
  connectRepositorySuccess,
  connectRepositoryFailure,
  deleteRepositoryStart,
  deleteRepositorySuccess,
  deleteRepositoryFailure,
  updateRepositoryBranchStart,
  updateRepositoryBranchSuccess,
  updateRepositoryBranchFailure,
  syncRepositoryStart,
  syncRepositorySuccess,
  syncRepositoryFailure,
  clearErrors,
  setSearchFilter,
  setProviderFilter,
  setBranchFilter,
  setStatusFilter,
  setLastSyncedFilter,
  setShowPendingOnly,
  resetFilters,
  setPage,
  setLimit,
  setSorting,
  resetRepository,
  setLoading
} = repositorySlice.actions;

export default repositorySlice.reducer;
